import { createQueryKeys } from '@lukemorales/query-key-factory';
import type { StoxlyClient } from '@stoxly/api-client';
import type { Body } from '../op-types';

export function createWatchlistQueries(client: StoxlyClient) {
  return createQueryKeys('watchlists', {
    // ['watchlists', 'all']
    all: {
      queryKey: null,
      queryFn: ({ signal }) => client.watchlistsFindAll({ signal }),
    },

    // ['watchlists', 'detail', watchlistId]
    detail: (watchlistId: string) => ({
      queryKey: [watchlistId],
      queryFn: ({ signal }) => client.watchlistsFindOne({ params: { id: watchlistId }, signal }),
    }),

    // ['watchlists', 'pinnedItems'] — pinned items across ALL of the user's
    // watchlists (the fox research sidebar's "Przypięte" section, F0).
    pinnedItems: {
      queryKey: null,
      queryFn: ({ signal }) => client.watchlistsGetPinnedItems({ signal }),
    },

    // ['watchlists', 'rows', watchlistId] — ONE batch (quote + market cap + P/E +
    // Stoxly Points) for every item on the list, correctness-or-null per field.
    // Separate from `detail` on purpose: the detail response is the list + its
    // items (the hub's contract), this one is the /investments table's row data
    // and refreshes on its own cadence.
    rows: (watchlistId: string) => ({
      queryKey: [watchlistId],
      queryFn: ({ signal }) => client.watchlistRowsGetRows({ params: { id: watchlistId }, signal }),
    }),
  });
}

export function createWatchlistMutations(client: StoxlyClient) {
  return {
    create: {
      mutationFn: (body: Body<'watchlistsCreate'>) => client.watchlistsCreate(body),
    },
    update: {
      mutationFn: ({ id, body }: { id: string; body: Body<'watchlistsUpdate'> }) =>
        client.watchlistsUpdate(body, { params: { id } }),
    },
    delete: {
      mutationFn: (id: string) => client.watchlistsDelete(undefined, { params: { id } }),
    },
    addItem: {
      mutationFn: ({ id, body }: { id: string; body: Body<'watchlistsAddItem'> }) =>
        client.watchlistsAddItem(body, { params: { id } }),
    },
    removeItem: {
      mutationFn: ({ id, itemId }: { id: string; itemId: string }) =>
        client.watchlistsRemoveItem(undefined, { params: { id, itemId } }),
    },
    // Pin/unpin an item in the fox research sidebar (F0).
    updateItem: {
      mutationFn: ({
        id,
        itemId,
        body,
      }: {
        id: string;
        itemId: string;
        body: Body<'watchlistsUpdateItem'>;
      }) => client.watchlistsUpdateItem(body, { params: { id, itemId } }),
    },
  };
}
